/* seo-core.js — pure SEO logic shared by the browser (src/seo.js) and the build-time
   prerenderer (scripts/prerender-heads.mjs). NO React / DOM / browser APIs here —
   this module must be importable from Node. Translation is injected as trFn(lang, s). */

import { CASES, BLOG_POSTS, PROVIDERS, IND_DATA, SERVICE_DATA } from "./data.js";
import { GEO_PAGES } from "./landing.js";

export const ORIGIN = "https://clinch.agency";
export const LANGS = ["en", "uk", "ru", "es", "de", "pt", "ar"];
/* Languages eligible for indexing. EN is the primary language and is always indexed; a non-EN
   language is indexed for a given page ONLY when that page's main content is actually translated
   there — decided per page by pageIndexLangs() below (a page that would show English stays
   noindex with no hreflang pointing to it). robots, hreflang alternates and the sitemap all use
   pageIndexLangs, not this bare list. */
export const INDEX_LANGS = ["en", "uk", "ru", "es", "de", "pt", "ar"];
export const OG_LOCALE = { en:"en_US", uk:"uk_UA", ru:"ru_RU", es:"es_ES", de:"de_DE", pt:"pt_PT", ar:"ar_AR" };

/* One {t: title, d: description} per static route. English is the DICT lookup key —
   translations come from src/_tr/<lang>_seo.json via tr(). Keep t <= 60 chars, d <= 160. */
export const SEO_META = {
  home:          { t:"Clinch — Crypto, Forex & iGaming Marketing Agency", d:"Performance marketing for Crypto, Web3, Forex and iGaming. KOL campaigns, PR, community and affiliate growth — built by industry insiders. Book a free call." },
  marketplace:   { t:"Crypto & iGaming Affiliate Programs Marketplace | Clinch", d:"Compare 30+ vetted affiliate programs — Binance, OKX, Bybit, top casinos and forex brokers. Commissions, payout terms and reviews in one place." },
  services:      { t:"Crypto, Web3 & iGaming Marketing Services | Clinch", d:"Full-stack growth services: KOL marketing, PR, social media, community management, fundraising and branding for Crypto, Forex and iGaming brands." },
  kol:           { t:"Crypto KOL & Influencer Marketing Agency | Clinch", d:"KOL campaigns with vetted crypto influencers on YouTube, X and Telegram. Performance-based deals, verified reach and tracked conversions." },
  affiliate:     { t:"Affiliate Program Management — Crypto & iGaming | Clinch", d:"Managed affiliate programs for exchanges, casinos and brokers — 200+ active partners, anti-fraud vetting and full-funnel tracking." },
  pr:            { t:"Crypto PR Agency — Tier-1 Media Coverage | Clinch", d:"Get featured in CoinDesk, Cointelegraph, The Block and 500+ outlets. Press releases, thought leadership and crisis PR for Web3 brands." },
  fundraising:   { t:"Web3 Fundraising Agency — Investor Intros | Clinch", d:"Raise your round with warm investor intros, pitch refinement and token sale marketing. $100M+ raised for Web3 clients." },
  smm:           { t:"Crypto Social Media Marketing Agency | Clinch", d:"Grow X, Telegram, LinkedIn and TikTok audiences that convert. Content, memes, ads and analytics for Crypto and iGaming brands." },
  community:     { t:"Telegram & Discord Community Management | Clinch", d:"24/7 multilingual community management for Web3 projects — moderation, engagement programs, ambassador networks and growth campaigns." },
  advisory:      { t:"Web3 Go-To-Market Strategy & Advisory | Clinch", d:"GTM strategy from ex-KuCoin and Binance insiders: positioning, tokenomics review, launch plans and channel mix that actually converts." },
  branding:      { t:"Web3 Branding & Design Agency | Clinch", d:"Brand strategy, visual identity, websites and pitch decks for Crypto, DeFi and iGaming projects. Stand out and be trusted." },
  studio:        { t:"Venture Studio — Product & Growth | Clinch", d:"From idea to launch: product development, smart contracts, design and go-to-market under one roof at the Clinch venture studio." },
  industries:    { t:"Industries We Grow: Crypto, Forex, iGaming | Clinch", d:"Deep vertical expertise across Crypto, Web3, DeFi, GameFi, Forex, Fintech, iGaming and Betting — see how we grow each market." },
  crypto:        { t:"Crypto & Web3 Marketing Agency | Clinch", d:"Token launches, exchange growth and Web3 user acquisition by a team from inside KuCoin, Binance and HBTC. 50+ crypto cases. Book a free call." },
  forex:         { t:"Forex Broker Marketing Agency | Clinch", d:"Trader acquisition, IB network growth and localized campaigns for forex and CFD brokers across CIS, MENA, LATAM and Asia." },
  igaming:       { t:"iGaming & Casino Marketing Agency | Clinch", d:"Player acquisition for casinos, sportsbooks and crypto gaming. Affiliates, streamers, SEO and retention — worked with Stake and licensed operators." },
  defi:          { t:"DeFi Marketing Agency — TVL & User Growth | Clinch", d:"Grow TVL, wallets and governance participation. KOLs, incentive campaigns and PR for DeFi protocols, DEXes and yield platforms." },
  "ai-web3":     { t:"AI x Web3 Marketing Agency | Clinch", d:"Positioning and growth for AI x crypto projects — agents, DePIN and data networks. Cut through the noise with insider-led campaigns." },
  exchanges:     { t:"Crypto Exchange Marketing & User Acquisition | Clinch", d:"100K+ verified traders delivered to Binance, Bybit and KuCoin. Listings buzz, trading competitions, KOLs and regional growth for exchanges." },
  gamefi:        { t:"GameFi & Web3 Gaming Marketing Agency | Clinch", d:"Player-first growth for GameFi and Web3 games: guilds, streamers, quests, community raids and token launch support." },
  wallets:       { t:"Crypto Wallet Marketing & User Growth | Clinch", d:"Drive installs, activation and retention for custodial and non-custodial wallets. ASO, KOLs, partnerships and DeFi integrations." },
  "mini-apps":   { t:"Telegram Mini App Marketing Agency | Clinch", d:"Launch and scale Telegram Mini Apps: viral loops, tap-to-earn mechanics, channel seeding and conversion to on-chain users." },
  launchpad:     { t:"Token Launch & IDO Marketing Agency | Clinch", d:"Full launch support: narrative, KOL rounds, launchpad relations, community hype and post-listing momentum. $100M+ raised." },
  cases:         { t:"Case Studies — Crypto, Forex & iGaming Growth | Clinch", d:"48 real growth stories with numbers: exchanges, casinos, DeFi protocols and brokers. See the strategies and the results." },
  clients:       { t:"Our Clients — Exchanges, Casinos & Brokers | Clinch", d:"Brands we've grown: Binance, KuCoin, Bybit, Stake and dozens of Web3, Forex and iGaming companies worldwide." },
  testimonials:  { t:"Client Testimonials & Reviews | Clinch", d:"What founders, CMOs and affiliate managers say about working with Clinch — real feedback from Crypto, Forex and iGaming teams." },
  blog:          { t:"Crypto & iGaming Marketing Blog | Clinch", d:"Playbooks, market breakdowns and honest takes on Crypto, Web3, Forex and iGaming marketing — from the team running the campaigns." },
  editorial:     { t:"Editorial Standards | Clinch", d:"How we research, write and fact-check content on the Clinch blog — our editorial principles and correction policy." },
  about:         { t:"About Clinch — Crypto Marketing Agency Built by Insiders", d:"We grew up inside crypto: ex-KuCoin, Binance and HBTC team, 8 years in the industry, 15 specialists across 25 countries." },
  referral:      { t:"Referral Program — Earn by Referring Clients | Clinch", d:"Introduce projects to Clinch and earn a share of every deal. Transparent terms, on-time payouts, no cap on earnings." },
  "referral-apply":{ t:"Apply to the Clinch Referral Program", d:"Join the Clinch referral network in two minutes — tell us about yourself and start earning on every referred client." },
  careers:       { t:"Careers — Crypto & Web3 Marketing Jobs | Clinch", d:"Remote-first roles in marketing, BD and creative for people who live and breathe Crypto, Forex and iGaming. See open positions." },
  contacts:      { t:"Contact Clinch — Get a Free Strategy Call", d:"Tell us about your project — we'll reply within 24 hours with a plan. Email, Telegram, WhatsApp or the form: pick your channel." },
  privacy:       { t:"Privacy Policy | Clinch", d:"How Clinch collects, uses and protects your personal data across our website and services." },
  terms:         { t:"Terms of Service | Clinch", d:"The terms and conditions that govern the use of the Clinch website and services." },
  cookies:       { t:"Cookie Policy | Clinch", d:"What cookies the Clinch website uses, why we use them and how you can control them." },
};

/* Register GEO/vertical landing pages into SEO_META so the prerenderer, sitemap and
   resolveMeta pick them up automatically (they enumerate Object.keys(SEO_META)). */
for (const [slug, g] of Object.entries(GEO_PAGES)) SEO_META[slug] = { t: g.metaT, d: g.metaD };

/* Marketplace category SEO pages: /marketplace/<slug> gets its OWN title, description,
   H1 and self-canonical instead of duplicating /marketplace. `filter` is the industry
   value the MarketplacePage pre-selects. Priority categories only (enough offers each). */
export const MARKETPLACE_CATS = {
  crypto:  { filter:"Crypto",  h1:"Crypto Affiliate Programs",
    t:"Crypto Affiliate Programs — Exchanges & Web3 | Clinch",
    d:"Compare top crypto affiliate programs — exchanges, wallets and Web3 offers with RevShare and CPA. Commissions, payout terms and reviews in one place.",
    intro:"Vetted crypto affiliate programs — exchanges, wallets and Web3 platforms. Compare RevShare, CPA and hybrid deals, payout terms and reviews." },
  forex:   { filter:"Forex",   h1:"Forex Affiliate Programs",
    t:"Forex Affiliate Programs — Broker CPA & RevShare | Clinch",
    d:"Compare forex broker affiliate programs — CPA, RevShare and IB deals from regulated brokers. Payout terms, GEOs and reviews side by side.",
    intro:"Forex and CFD broker affiliate programs — CPA, RevShare and IB deals from regulated brokers. Compare payouts, GEOs and terms." },
  igaming: { filter:"iGaming", h1:"iGaming & Casino Affiliate Programs",
    t:"iGaming & Casino Affiliate Programs | Clinch",
    d:"Compare iGaming affiliate programs — online casinos and sportsbooks with RevShare, CPA and hybrid deals. Payout terms, GEOs and reviews in one place.",
    intro:"Casino and sportsbook affiliate programs — RevShare, CPA and hybrid deals from top iGaming brands. Compare payouts, GEOs and terms." },
};
export const mktCat = param => (param ? MARKETPLACE_CATS[String(param).toLowerCase()] : null) || null;

/* Which languages this page may be indexed in. EN is always included (primary language).
   A non-EN language is included only when the page's representative main-content string is
   actually translated there (tr returns something other than the English source). Used to gate
   robots (index/noindex), hreflang alternates and sitemap inclusion per page — so a page whose
   body is still English (e.g. a not-yet-translated post) stays noindex in that language, and no
   hreflang points to it. trFn is the injected translator (tr(lang, string)). */
export function pageIndexLangs(page, param, trFn) {
  const tr = trFn || ((l, s) => s);
  let key; // representative main-content string that must be translated for the page to count
  if (page === "blog_post") { const p = BLOG_POSTS.find(x => x.id === param); key = p && p.body; }
  else if (page === "case_post") { const c = CASES.find(x => x.id === param); key = c && c.body; }
  else if (page === "offer") { const p = PROVIDERS.find(x => x.id === param); key = p && (p.desc || p.headline); }
  else if (page === "marketplace" && mktCat(param)) key = mktCat(param).d;
  /* Industry & service pages render their body from IND_DATA / SERVICE_DATA, NOT from the meta
     description — so gate them on that rendered body (the `sub` line). A page whose meta is
     translated but whose body is still English (e.g. /defi, /affiliate) must stay noindex in
     that language. The meta description alone would falsely mark it ready. */
  else if (IND_DATA[page]) key = IND_DATA[page].sub || IND_DATA[page].hero;
  else if (SERVICE_DATA[page]) key = SERVICE_DATA[page].sub || SERVICE_DATA[page].title;
  else { const m = SEO_META[page]; key = m && m.d; }
  return INDEX_LANGS.filter(l => l === "en" || (!!key && tr(l, key) !== key));
}

/* Short, brand-based slugs for case URLs: /case-studies/<brand> (e.g. /case-studies/binance).
   Brands are unique across CASES, so id <-> slug is bijective. caseIdFromSlug falls back to
   treating an unknown value as a raw id, so legacy /case/<id> params still resolve. */
const caseSlugify = s => String(s).toLowerCase().replace(/[.\s_]+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "");
export const CASE_SLUG = {};        // id -> slug
export const CASE_ID_BY_SLUG = {};  // slug -> id
for (const c of CASES) { const s = caseSlugify(c.brand || c.id); CASE_SLUG[c.id] = s; CASE_ID_BY_SLUG[s] = c.id; }
export const caseSlug = id => CASE_SLUG[id] || id;
export const caseIdFromSlug = slug => CASE_ID_BY_SLUG[slug] || slug;

/* Mirrors toPath() in nexgrow_v2.jsx (kept local to avoid a circular import). */
export function pathFor(page, param, lang) {
  const pre = lang && lang !== "en" ? "/" + lang : "";
  if (page === "home") return pre ? pre + "/" : "/";
  if (page === "blog_post") return pre + "/blog/" + encodeURIComponent(param);
  if (page === "case_post") return pre + "/case-studies/" + encodeURIComponent(caseSlug(param));
  if (page === "cases") return pre + "/case-studies";
  if (page === "contacts") return pre + "/contact";
  return pre + "/" + page + (param ? "/" + encodeURIComponent(param) : "");
}

export const parseDate = s => {
  const d = new Date(s);
  if (isNaN(d)) return null;
  // format from local date parts — toISOString() would shift "Feb 18" back a day east of UTC
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const clip = (s, n) => { s = (s || "").trim(); return s.length <= n ? s : s.slice(0, n - 1).replace(/\s+\S*$/, "") + "…"; };

const ORG_REF = { "@type": "Organization", name: "Clinch", url: ORIGIN + "/", logo: { "@type": "ImageObject", url: ORIGIN + "/favicon-192.png", width: 192, height: 192 } };

/* FAQ shown on the home page (HomeFAQ component) — must stay in sync with the rendered copy.
   Note: Google retired FAQ rich results (May 2026); kept because the markup still helps
   content understanding and costs nothing. */
const HOME_FAQ = [
  { q:"How quickly can we launch?", a:"Most campaigns can be prepared and launched within a few days, depending on scope, assets and partner approvals." },
  { q:"Do you work on fixed fee, CPA or hybrid models?", a:"All of them. We run fixed retainers, performance-based CPA / revenue-share, or a hybrid — whichever best fits your goals, budget and risk appetite." },
  { q:"Which markets do you cover?", a:"Crypto, Web3, Forex, Fintech, iGaming and Betting — worldwide, with especially strong reach across CIS, MENA, LATAM, Europe and Asia." },
  { q:"Can you support both brands and affiliates?", a:"Yes. Brands get vetted partners and fully managed campaigns; affiliates, KOLs and media get premium offers with on-time payouts through the Clinch Marketplace." },
  { q:"Do you provide transparent reporting?", a:"Always. You receive regular reports with real numbers — reach, conversions, spend and clear next steps — no vanity metrics." },
  { q:"How do we get started?", a:"Book a quick intro call. We'll review your goals and current setup, then map out the right channel mix and a plan you can act on." },
];

const SERVICE_PAGES = {
  kol:"KOL & Influence Marketing", affiliate:"Affiliate Marketing", pr:"PR & Earned Media",
  fundraising:"Fundraising", smm:"Social Media Marketing", community:"Community Management",
  advisory:"Advisory & Strategy", branding:"Branding", studio:"Venture Studio",
};
const INDUSTRY_PAGES = {
  crypto:"Crypto & Web3 Marketing", forex:"Forex Broker Marketing", igaming:"iGaming & Casino Marketing",
  defi:"DeFi Marketing", "ai-web3":"AI & Web3 Marketing", exchanges:"Crypto Exchange Marketing",
  gamefi:"GameFi Marketing", wallets:"Crypto Wallet Marketing", "mini-apps":"Telegram Mini App Marketing",
  launchpad:"Token Launch Marketing",
};

function breadcrumb(items, lang) {
  const pre = lang && lang !== "en" ? "/" + lang : "";
  const abs = p => ORIGIN + (p === "/" ? (pre ? pre + "/" : "/") : pre + p);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it[0],
      ...(it[1] ? { item: abs(it[1]) } : {}),
    })),
  };
}

export function buildJsonLd(page, param, title, lang = "en", trFn) {
  const blocks = [];
  const tr = trFn || ((l, s) => s);           // translate content fields to match the page language
  const T = s => tr(lang, s);
  const abs = p => ORIGIN + pathFor(p === "home" ? "home" : page, param, lang);
  if (page === "home") {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: HOME_FAQ.map(f => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    return blocks;
  }
  if (SERVICE_PAGES[page]) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: T(SERVICE_PAGES[page]),
      serviceType: T(SERVICE_PAGES[page]),
      provider: ORG_REF,
      areaServed: "Worldwide",
      url: ORIGIN + pathFor(page, null, lang),
    });
    blocks.push(breadcrumb([[T("Home"), "/"], [T("Services"), "/services"], [T(SERVICE_PAGES[page]), "/" + page]], lang));
    return blocks;
  }
  if (INDUSTRY_PAGES[page]) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: T(INDUSTRY_PAGES[page]),
      serviceType: T(INDUSTRY_PAGES[page]),
      provider: ORG_REF,
      areaServed: "Worldwide",
      url: ORIGIN + pathFor(page, null, lang),
    });
    blocks.push(breadcrumb([[T("Home"), "/"], [T("Industries"), "/industries"], [T(INDUSTRY_PAGES[page]), "/" + page]], lang));
    return blocks;
  }
  if (page === "blog_post") {
    const p = BLOG_POSTS.find(x => x.id === param);
    if (p) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: T(p.title),
        description: clip(T(p.excerpt), 160),
        ...(p.img ? { image: [/^https?:/.test(p.img) ? p.img : ORIGIN + p.img] } : {}),
        ...(parseDate(p.date) ? { datePublished: parseDate(p.date), dateModified: parseDate(p.date) } : {}),
        author: { "@type": "Organization", name: p.author || "Clinch", url: ORIGIN + "/" },
        publisher: ORG_REF,
        mainEntityOfPage: { "@type": "WebPage", "@id": abs("blog_post") },
        articleSection: T(p.cat),
        inLanguage: lang,
      });
      blocks.push(breadcrumb([[T("Home"), "/"], [T("Blog"), "/blog"], [T(p.title), "/blog/" + encodeURIComponent(p.id)]], lang));
    }
    return blocks;
  }
  if (page === "case_post") {
    const c = CASES.find(x => x.id === param);
    if (c) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: T(c.headline),
        description: clip(T(c.excerpt), 160),
        ...(c.img ? { image: [c.img.startsWith("http") ? c.img : ORIGIN + c.img] } : {}),
        datePublished: "2025-01-01",
        dateModified: "2025-01-01",
        author: ORG_REF,
        publisher: ORG_REF,
        mainEntityOfPage: { "@type": "WebPage", "@id": abs("case_post") },
        articleSection: T("Case Study"),
        about: T(c.industry),
        inLanguage: lang,
      });
      blocks.push(breadcrumb([[T("Home"), "/"], [T("Case Studies"), "/case-studies"], [T(c.headline), "/case-studies/" + encodeURIComponent(caseSlug(c.id))]], lang));
    }
    return blocks;
  }
  if (page === "offer") {
    const p = PROVIDERS.find(x => x.id === param);
    if (p) {
      /* Rating + review count are rendered on the offer page, and the rating is about a
         third-party program (not Clinch itself), so aggregateRating markup is eligible. */
      blocks.push({
        "@context": "https://schema.org",
        "@type": "Product",
        name: p.name,
        description: clip(T(p.desc || p.headline), 300),
        image: [ORIGIN + "/og-image.png"],
        brand: { "@type": "Brand", name: p.name.replace(/\s*Affiliate Program\s*$/i, "") },
        url: ORIGIN + pathFor("offer", p.id, lang),
        ...(p.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.reviews || 1, bestRating: 5 } } : {}),
      });
      blocks.push(breadcrumb([[T("Home"), "/"], [T("Marketplace"), "/marketplace"], [p.name, "/offer/" + encodeURIComponent(p.id)]], lang));
    }
    return blocks;
  }
  if (GEO_PAGES[page]) {
    const g = GEO_PAGES[page];
    const hub = { Crypto: "crypto", iGaming: "igaming", Forex: "forex" }[g.vertical] || "industries";
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: T(g.h1),
      serviceType: g.vertical + " Marketing",
      provider: ORG_REF,
      areaServed: T(g.region),
      url: ORIGIN + pathFor(page, null, lang),
    });
    if (g.faq && g.faq.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: g.faq.map(f => ({ "@type": "Question", name: T(f.q), acceptedAnswer: { "@type": "Answer", text: T(f.a) } })),
      });
    }
    blocks.push(breadcrumb([[T("Home"), "/"], [T(INDUSTRY_PAGES[hub] || "Industries"), "/" + hub], [T(g.h1), "/" + page]], lang));
    return blocks;
  }
  if (SEO_META[page] && page !== "home") {
    blocks.push(breadcrumb([[T("Home"), "/"], [title.replace(/\s*\|\s*Clinch.*$/, ""), "/" + page]], lang));
  }
  return blocks;
}

/* Resolve LOCALIZED {t, d, ogType} for the current route. Dynamic pages translate their
   content pieces (post titles/excerpts already live in DICT via the blog/case partials)
   BEFORE composing the final string — a composed string would never match a DICT key. */
export function resolveMeta(page, param, lang, trFn) {
  const tr = trFn || ((l, s) => s);
  if (page === "blog_post") {
    const p = BLOG_POSTS.find(x => x.id === param);
    if (p) return { t: clip(tr(lang, p.title), 50) + " | Clinch", d: clip(tr(lang, p.excerpt), 160), ogType: "article", date: parseDate(p.date), img: p.img && !/^https?:/.test(p.img) ? ORIGIN + p.img : p.img };
  }
  if (page === "case_post") {
    const c = CASES.find(x => x.id === param);
    if (c) return { t: clip(tr(lang, c.headline), 40) + " | " + tr(lang, "Clinch Case Study"), d: clip(tr(lang, c.excerpt), 160), ogType: "article" };
  }
  if (page === "offer") {
    const p = PROVIDERS.find(x => x.id === param);
    if (p) return { t: clip(p.name, 29) + " — " + tr(lang, "Review & Commission") + " | Clinch", d: clip(tr(lang, p.headline || p.desc), 160), ogType: "website" };
  }
  if (page === "marketplace") {
    const c = mktCat(param);
    if (c) return { t: tr(lang, c.t), d: tr(lang, c.d), ogType: "website" };
  }
  const m = SEO_META[page];
  if (m) return { t: tr(lang, m.t), d: tr(lang, m.d), ogType: "website" };
  return null; // unknown route — soft-404 (router falls back to home): noindex it
}
